import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseTransactionPrettyType } from '../../utils/transaction-type';
import { convertValueToUSD } from '../../../../helpers/currency.helper';
import { transactionDescription } from '../../utils/transaction-description';
import { transactionIconImageName } from '../../utils/transaction-icon';
import { TransactionCheckStatus } from '../../enum/transaction-check-status';
import Heading from '../../../../generic-components/Heading';
import { useStore } from '../../../../store';
import { LiliToolTip } from '../../../../generic-components/LiliToolTip';

const iconPath = 'https://assets.lili.co/webapp/transactionsv2/';

interface IProps {
  shouldHideBorder?: boolean;
}

export const TransactionDetailsHeader = ({ shouldHideBorder }: IProps) => {
  const { t: translate } = useTranslation();
  const transaction = useStore((state) => state.selectedTransaction);
  const transactionType = useMemo(() => parseTransactionPrettyType(transaction!, translate), [transaction]);
  const description = useMemo(() => transactionDescription(transaction!), [transaction]);
  const iconName = transactionIconImageName(transaction!);

  // Utils for check status
  const checkStatus = transaction?.checkDetailsData?.status;
  const rejectedCheck = checkStatus === TransactionCheckStatus.REJECT;
  const bouncedCheck = checkStatus === TransactionCheckStatus.BOUNCED;

  const isIncome = (transaction?.amount || 0) > 0;
  const isExpense = !isIncome;

  const ttStyles = `fadeIn before:w-[10px] before:h-[10px] before:bg-bw07 before:border-bw07 before:border-b before:border-r before:absolute
        before:left-[50%] before:translate-x-[-50%] before:rotate-[45deg] before:translate-y-[55%] before:bottom-0 absolute z-[9999] top-0 left-[50%] translate-x-[-50%]
        translate-y-[calc(-100%-10px)] bg-bw07 border border-bw07 px-[12px] py-[6px] rounded-[6px] shadow-light flex items-center justify-center w-fit max-w-[100%]`;

  const parsedAmountString = () => {
    const amount = Math.abs(transaction?.amount || 0);
    const parsedAmount = convertValueToUSD(amount, 2).replace('$', '');

    const getAmountClasses = () => {
      let classNames = 'flex items-center font-metricMedium';

      if (rejectedCheck || bouncedCheck) {
        classNames += ' line-through text-bw05';
      } else if (isExpense) {
        classNames += ' text-bw07';
      } else if (isIncome) {
        classNames += ' text-badgeGreen03';
      }

      return classNames;
    };

    return (
      <div className='font-metricR flex flex-col items-end'>
        <div className={getAmountClasses()}>
          <span className='text=[14px]'>{isExpense && '-'}</span>
          <div className='font-metricR text-[20px] px-[2px]'>$</div>
          <span id='parsedAmountValue' className='text-[24px] font-metricRSemibold'>
            {parsedAmount}
          </span>
        </div>
        {(rejectedCheck || bouncedCheck) && (
          <div className='text-[14px] text-red02'>
            {rejectedCheck && 'Rejected'}
            {bouncedCheck && 'Bounced'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex justify-between pb-[20px] gap-[6px] ${shouldHideBorder ? '' : 'border-b border-bw03'}`}>
      <div className='flex items-start gap-[8px]'>
        <div className='flex flex-col items-end'>
          <img src={`${iconPath}${iconName}.svg`} alt={iconName} className='w-[2rem]' />
          {transaction?.initiatorPan && (
            <Heading title={`**${transaction.initiatorPan}`} className='font-metricR text-action03 text-[10px]' />
          )}
        </div>
        <div className='flex flex-col'>
          <LiliToolTip
            childrenClassName={ttStyles}
            conditionalAppearance
            toolTipChildren={<p className='font-metricR text-[14px] text-bw00 break-words w-[200px]'>{description}</p>}
          >
            <p className='font-metricRMedium text-[18px] text-bw07 max-w-[200px] truncate'>{description}</p>
          </LiliToolTip>
          <p className='trxTypeSelector font-metricR text-[14px] text-bw07'>{transactionType}</p>
        </div>
      </div>
      {parsedAmountString()}
    </div>
  );
};
